import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    FormControlLabel,
    Box,
    Typography,
    TextField,
    InputAdornment,
    Chip,
    Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';

const CardSelectionModal = ({
    open,
    onClose,
    onConfirm,
    availableCards = [],
    selectedCards = [],
    title = 'Select Account Cards',
    direction = 'rtl'
}) => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [localSelectedCards, setLocalSelectedCards] = useState([]);

    useEffect(() => {
        setLocalSelectedCards(selectedCards);
    }, [selectedCards, open]);

    const filteredCards = availableCards.filter(card =>
        card.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        card.cardId?.toString().includes(searchQuery)
    );

    const cardSections = useMemo(() => {
        const sections = [];
        let currentPalCode = null;
        let currentSection = null;

        filteredCards.forEach((card) => {
            const palCode = card.palCode || t('NonPalCode');
            if (palCode !== currentPalCode) {
                currentPalCode = palCode;
                currentSection = { palCode, cards: [] };
                sections.push(currentSection);
            }
            currentSection.cards.push(card);
        });

        return sections;
    }, [filteredCards, t]);

    const handleCardToggle = (card) => {
        setLocalSelectedCards(prev => {
            const isSelected = prev.some(selected => selected.cardId === card.cardId);
            if (isSelected) {
                return prev.filter(selected => selected.cardId !== card.cardId);
            } else {
                return [...prev, card];
            }
        });
    };

    const handleSelectAll = () => {
        setLocalSelectedCards(filteredCards);
    };

    const handleDeselectAll = () => {
        setLocalSelectedCards([]);
    };

    const handleConfirm = () => {
        onConfirm(localSelectedCards);
        onClose();
    };

    const handleCancel = () => {
        setLocalSelectedCards(selectedCards);
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleCancel}
            maxWidth="md"
            fullWidth
            dir={direction}
        >
            <DialogTitle
                sx={{
                    background: 'linear-gradient(90deg, #304FFF 0%, #1E88E5 100%)',
                    color: 'white',
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{t(title)}</Typography>
                    <Box>
                        <Button 
                            sx={{ 
                                backgroundColor: '#304FFF', 
                                color: 'white',
                                mr: 1,
                                '&:hover': {
                                    backgroundColor: '#1E88E5'
                                }
                            }}
                            size="small" 
                            onClick={handleSelectAll}
                        >
                            {t('SelectAll')}
                        </Button>
                        <Button 
                            sx={{ 
                                backgroundColor: '#304FFF', 
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: '#1E88E5'
                                }
                            }}
                            size="small" 
                            onClick={handleDeselectAll}
                        >
                            {t('DeselectAll')}
                        </Button>
                    </Box>
                </Box>
            </DialogTitle>
            
            <DialogContent>
                <Box mb={2} style={{ marginTop: '10px' }}>
                    <TextField
                        fullWidth
                        placeholder={t('SearchCards')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        size="small"
                    />
                </Box>

                <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                        {t('SelectedCards')}: {localSelectedCards.length} / {availableCards.length}
                    </Typography>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Box 
                    sx={{ 
                        maxHeight: 400, 
                        overflowY: 'auto',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        p: 1
                    }}
                >
                    {filteredCards.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                            {t('NoCardsFound')}
                        </Typography>
                    ) : (
                        cardSections.map((section) => (
                            <Box key={section.palCode} sx={{ mb: 1.5 }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        px: 1,
                                        py: 0.75,
                                        mb: 0.5,
                                        borderRadius: 1,
                                        backgroundColor: '#eef2ff',
                                        color: '#304FFF',
                                        fontWeight: 700,
                                        fontSize: '12px',
                                        textAlign: direction === 'rtl' ? 'right' : 'left',
                                        direction: direction === 'rtl' ? 'rtl' : 'ltr',
                                    }}
                                >
                                    {section.palCode}
                                </Typography>
                                {section.cards.map((card) => {
                                    const isSelected = localSelectedCards.some(selected => selected.cardId === card.cardId);
                                    return (
                                        <FormControlLabel
                                            key={card.cardId}
                                            control={
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={() => handleCardToggle(card)}
                                                    size="small"
                                                />
                                            }
                                            label={
                                                <Box display="flex" alignItems="center" width="100%">
                                                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                                        {card.cardId} - {card.name}
                                                    </Typography>
                                                </Box>
                                            }
                                            sx={{
                                                width: '100%',
                                                margin: 0,
                                                pl: 1,
                                                '& .MuiFormControlLabel-label': {
                                                    width: '100%'
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        ))
                    )}
                </Box>

                {localSelectedCards.length > 0 && (
                    <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('SelectedCards')}:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {localSelectedCards.map((card) => (
                                <Chip
                                    key={card.cardId}
                                    label={`${card.cardId} - ${card.name}`}
                                    size="small"
                                    onDelete={() => handleCardToggle(card)}
                                    color="primary"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            
            <DialogActions>
                <Button onClick={handleCancel}>
                    {t('Cancel')}
                </Button>
                <Button 
                    onClick={handleConfirm} 
                    variant="contained"
                    disabled={localSelectedCards.length === 0}
                >
                    {t('Confirm')} ({localSelectedCards.length})
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CardSelectionModal;
